/**
 * Speaker Detection feature documentation page
 */

import {DocsContent} from '../../../components/docs/DocsContent';
import {CodeBlock} from '../../../components/docs/CodeBlock';
import {DocsActionButtons} from '../../../components/docs/DocsActionButtons';
import {Link} from 'react-router-dom';

export function SpeakerDetection() {
  return (
    <DocsContent
      title="Speaker Detection"
      description="Automatically identify and label speakers in your meetings."
    >
      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Overview</h2>
          <p className="text-textSecondary mb-4">
            VoxaPilot automatically identifies different speakers in your meetings and labels them
            throughout the transcript. This makes it easy to see who said what and track
            participation.
          </p>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">How It Works</h2>
          <p className="text-textSecondary mb-4">
            Speaker detection uses voice recognition to distinguish between different speakers:
          </p>

          <ol className="list-decimal list-inside space-y-2 text-textSecondary mb-6">
            <li>Analyzes voice characteristics (pitch, tone, cadence)</li>
            <li>Groups similar voices together</li>
            <li>Assigns labels (Speaker 1, Speaker 2, etc.)</li>
            <li>Maintains consistency across the entire transcript</li>
          </ol>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Managing Speakers</h2>
          <p className="text-textSecondary mb-4">
            You can customize speaker labels and manage speaker identification:
          </p>

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Assigning Names</h3>
          <p className="text-textSecondary mb-4">
            Replace generic labels (Speaker 1, Speaker 2) with actual names:
          </p>

          <CodeBlock
            code={`// Update speaker name
await api.meetings.updateSpeaker(meetingId, {
  speakerId: 'speaker-1',
  name: 'John Doe',
  email: 'john@example.com',
});

// The transcript will be updated with the new name`}
            language="javascript"
          />

          <h3 className="text-xl font-semibold text-textPrimary mb-3 mt-6">Merging Speakers</h3>
          <p className="text-textSecondary mb-4">
            If the system incorrectly split one person into multiple speakers, you can merge them:
          </p>

          <CodeBlock
            code={`// Merge two speakers
await api.meetings.mergeSpeakers(meetingId, {
  speakerIds: ['speaker-1', 'speaker-3'],
  targetName: 'John Doe',
});`}
            language="javascript"
          />
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Speaker Statistics</h2>
          <p className="text-textSecondary mb-4">
            View participation metrics for each speaker:
          </p>

          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Total speaking time</li>
            <li>Number of contributions</li>
            <li>Average contribution length</li>
            <li>Speaking frequency over time</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Best Practices</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary mb-6">
            <li>Review speaker labels after transcription</li>
            <li>Assign names early for better accuracy</li>
            <li>Use consistent naming across meetings</li>
            <li>Merge speakers if detection is incorrect</li>
            <li>Use speaker filters to focus on specific participants</li>
          </ul>
        </section>

        <section>
          <h2 className="text-2xl font-bold text-textPrimary mb-4">Next Steps</h2>
          <ul className="list-disc list-inside space-y-2 text-textSecondary dark:text-[#858585]">
            <li>
              <Link to="/docs/features/transcription" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Learn about transcription
              </Link>
            </li>
            <li>
              <Link to="/docs/features/analytics" className="text-gray-900 dark:text-orange-500 underline hover:text-gray-700 dark:hover:text-orange-400">
                Explore analytics
              </Link>
            </li>
          </ul>
        </section>

        <DocsActionButtons pageTitle="Speaker Detection" />
      </div>
    </DocsContent>
  );
}

